import React from 'react';
import { createPortal } from 'react-dom';
import AIImage from './AIImage';
import GlowCard from './GlowCard';

export type CarouselImage = {
  src: string;
  alt: string;
};

export const usePublicImages = (folder: string, altPrefix: string) => {
  const [images, setImages] = React.useState<CarouselImage[]>([]);

  React.useEffect(() => {
    let isMounted = true;

    const loadImages = async () => {
      try {
        const response = await fetch(`/api/public-assets/${folder}`);
        if (!response.ok) return;
        const payload = await response.json();
        if (!Array.isArray(payload?.images)) return;
        const nextImages = payload.images.map((src: string, index: number) => ({
          src,
          alt: `${altPrefix} ${index + 1}`,
        }));
        if (isMounted) {
          setImages(nextImages);
        }
      } catch (error) {
        console.error(`Failed to load ${folder} images`, error);
      }
    };

    loadImages();

    return () => {
      isMounted = false;
    };
  }, [folder, altPrefix]);

  return images;
};

type ImageCarouselProps = {
  title: string;
  description?: string;
  images: CarouselImage[];
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({ title, description, images }) => {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);
  const imageCount = images.length;

  React.useEffect(() => {
    if (activeIndex >= imageCount && imageCount > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, imageCount]);

  React.useEffect(() => {
    if (imageCount <= 1 || isPaused) return;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % imageCount);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [imageCount, isPaused]);

  React.useEffect(() => {
    if (expandedIndex === null) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedIndex(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [expandedIndex]);

  if (imageCount === 0) {
    return null;
  }

  const goTo = (index: number) => {
    const nextIndex = (index + imageCount) % imageCount;
    setActiveIndex(nextIndex);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(activeIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(activeIndex + 1);
    }
  };

  const handleOpen = (index: number) => {
    setExpandedIndex(index);
  };

  const handleClose = () => {
    setExpandedIndex(null);
  };

  return (
    <section className="py-24 px-4 border-b border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-3 text-white">{title}</h2>
            {description ? (
              <p className="text-slate-400 text-base leading-relaxed max-w-3xl">{description}</p>
            ) : null}
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            {imageCount} {imageCount === 1 ? 'Screen' : 'Screens'}
          </div>
        </div>
        <GlowCard className="p-6">
          <div
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60 aspect-[16/9]"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            onKeyDown={handleKeyDown}
            role="region"
            aria-label={`${title} carousel`}
            tabIndex={0}
          >
            <div
              className="flex h-full transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {images.map((image, index) => (
                <button
                  key={image.src}
                  type="button"
                  className="min-w-full h-full cursor-zoom-in bg-transparent border-0 p-0 focus:outline-none"
                  onClick={() => handleOpen(index)}
                  aria-label={`Expand ${image.alt}`}
                >
                  <AIImage
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full rounded-none"
                    aspectRatio="16:9"
                    fit="contain"
                  />
                </button>
              ))}
            </div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-black/35 via-transparent to-transparent" />
          </div>
          {imageCount > 1 ? (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/30 transition-colors"
                  onClick={() => goTo(activeIndex - 1)}
                  aria-label="Previous slide"
                >
                  <span aria-hidden="true">{'<'}</span>
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/30 transition-colors"
                  onClick={() => goTo(activeIndex + 1)}
                  aria-label="Next slide"
                >
                  <span aria-hidden="true">{'>'}</span>
                </button>
                <span className="text-xs text-slate-500 uppercase tracking-widest">
                  Slide {activeIndex + 1} of {imageCount}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {images.map((_, index) => (
                  <button
                    key={`dot-${index}`}
                    type="button"
                    className={`h-2 rounded-full transition-all ${
                      index === activeIndex ? 'w-8 bg-accent' : 'w-4 bg-white/10 hover:bg-white/30'
                    }`}
                    onClick={() => goTo(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </GlowCard>
      </div>
      {expandedIndex !== null && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm overflow-hidden"
              onClick={handleClose}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                className="fixed top-[calc(env(safe-area-inset-top)+16px)] right-[calc(env(safe-area-inset-right)+16px)] z-[70] bg-white/10 text-white border border-white/20 rounded-full w-11 h-11 flex items-center justify-center hover:bg-white/20 transition-colors"
                onClick={handleClose}
                aria-label="Close expanded view"
              >
                <span aria-hidden="true">x</span>
              </button>
              <div
                className="absolute left-1/2 top-1/2 w-[95vw] h-[95vh] -translate-x-1/2 -translate-y-1/2"
                onClick={(event) => event.stopPropagation()}
              >
                <AIImage
                  src={images[expandedIndex].src}
                  alt={images[expandedIndex].alt}
                  className="w-full h-full rounded-2xl"
                  aspectRatio="16:9"
                  fit="contain"
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
};

export default ImageCarousel;
